import { ModalId, ModalManagerContext } from "@/Contexts/ModalManagerContext";
import classNames from "@/utils/classNames";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, PropsWithChildren, useContext, useEffect } from "react";
import { IoClose } from "react-icons/io5";

export type ModalProps = PropsWithChildren<{
    id: ModalId;
    show: boolean;
    prepareToShow?: boolean;
    testId?: string;
    maxWidth?:
        | "sm"
        | "md"
        | "lg"
        | "xl"
        | "2xl"
        | "3xl"
        | "4xl"
        | "5xl"
        | "6xl"
        | "7xl"
        | "8xl"
        | "9xl";
    closeable?: boolean;
    showCloseButton?: boolean;
    animateClose?: boolean;
    onClose: CallableFunction;
    disableBackgroundClickToClose?: boolean;
}>;

export default function Modal({
    id,
    children,
    show = false,
    prepareToShow = false /* blocks lower priority modals from showing while a modal decides if it should show */,
    testId,
    maxWidth = "2xl",
    closeable = true,
    showCloseButton = false,
    animateClose = true /* Fixes weird effects caused by modal contents being dependent on removed state */,
    onClose = () => {},
    disableBackgroundClickToClose = false,
}: ModalProps) {
    const {
        isInModalManagerContext,
        modalToDisplay,
        askToDisplayModal,
        removeModalFromDisplay,
    } = useContext(ModalManagerContext);
    const canShow = modalToDisplay === id;

    const close = () => {
        if (closeable) {
            onClose();
        }
    };

    const maxWidthClass = {
        sm: "sm:max-w-sm",
        md: "sm:max-w-md",
        lg: "sm:max-w-lg",
        xl: "sm:max-w-xl",
        "2xl": "sm:max-w-2xl",
        "3xl": "sm:max-w-3xl",
        "4xl": "md:max-w-4xl",
        "5xl": "md:max-w-5xl",
        "6xl": "md:max-w-6xl",
        "7xl": "md:max-w-7xl",
        "8xl": "md:max-w-[1408px]",
        "9xl": "md:max-w-[1536px]",
    }[maxWidth];

    const queueModalToShow = show || prepareToShow;
    useEffect(() => {
        if (!queueModalToShow) {
            return;
        }
        askToDisplayModal(id);
        return () => removeModalFromDisplay(id);
    }, [queueModalToShow]);

    return (
        <Transition
            show={isInModalManagerContext ? canShow && show : show}
            as={Fragment}
            leave={animateClose ? "duration-200" : ""}
        >
            <Dialog
                as="div"
                id="modal"
                data-testid={testId}
                className="fixed bottom-0 z-50 flex h-dvh w-screen transform items-center justify-center overflow-y-auto px-4 py-6"
                onClose={close}
                tabIndex={0} // Here be dragons: https://stackoverflow.com/questions/7827004/how-to-avoid-automatic-focus-on-first-input-field-when-popping-a-html-form-as-a
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave={animateClose ? "ease-in duration-200" : ""}
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="absolute left-0 top-0 h-dvh w-screen bg-gray-500/75"
                        onClick={() =>
                            !disableBackgroundClickToClose && close()
                        }
                        role="presentation"
                    />
                </Transition.Child>

                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave={animateClose ? "ease-in duration-200" : ""}
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        id="modal-content"
                        className={classNames(
                            `z-10 max-h-full transform overflow-y-auto rounded-lg bg-white shadow-xl sm:mx-auto ${maxWidthClass}`,
                            animateClose ? "transition-all" : "",
                        )}
                    >
                        {showCloseButton && (
                            <div className="sticky top-0 h-0 z-10">
                                <IoClose
                                    data-testid="close-icon"
                                    className="absolute right-2 top-2 -mr-2 -mt-2 cursor-pointer rounded-bl-lg p-3 text-5xl transition-colors bg-brand-transparent hover:bg-gray-100"
                                    onClick={() => close()}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault();
                                            close();
                                        }
                                    }}
                                    aria-label="Close modal"
                                />
                            </div>
                        )}
                        {children}
                    </div>
                </Transition.Child>
            </Dialog>
        </Transition>
    );
}
